import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { LineSearchDropdown } from "@bloomberg/media-ui-dropdown";
import type { LineSearchDropdownProps } from "@bloomberg/media-ui-dropdown/types";
import { debounce } from "@foundation/util";
import { Plus } from "@bloomberg/media-ui-icons";
import MediaServicesSearchClient from "@foundation/media-services-search-client";
import { createClasses } from "@bloomberg/media-ui-core";
import type { WatchlistComparison } from "@foundation/watchlist-client";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import styles from "./styles.module.css";
import { usePageData } from "~context/PageContext";
import { getSessionIdCookie } from "~utils/sessionUtils";
import { SearchQueryResult } from "~types/types";
import { usePortfolioPage } from "~context/PortfolioPageContext";
import { normalizeComparison } from "~components/WatchlistComparisonChart/utils";

const MemoizedLineSearchDropdown = memo(LineSearchDropdown);
const INPUT_DELAY = 300;
const MAX_COMPARISONS_ALLOWED = 4;

type DropdownOptions = LineSearchDropdownProps["options"];

type SearchDropdownProps = {
  id: string;
  disabled: boolean;
  placeholder: string;
  onSelectValue: (optionId: string, value: string) => void;
  fetchSearchResults: (query: string) => Promise<DropdownOptions>;
};

function SearchDropdown(props: SearchDropdownProps) {
  const { id, disabled, placeholder, onSelectValue, fetchSearchResults } =
    props;

  const [suggestions, setSuggestions] = useState<DropdownOptions>([]);

  const clearSuggestions = () => {
    setSuggestions([]);
  };

  const debouncedFetchResults = debounce((searchQuery: string) => {
    if (!searchQuery) {
      clearSuggestions();

      return;
    }

    fetchSearchResults(searchQuery).then((results: DropdownOptions) => {
      if (!results) {
        clearSuggestions();

        return;
      }

      setSuggestions(results);
    });
  }, INPUT_DELAY);

  const onTextChange = (value: string) => {
    debouncedFetchResults(value);
  };

  const handleChange = useCallback(
    (
      option: DropdownOptions[0] | null,
      downshiftHandle: { setState: (value: unknown) => void }
    ) => {
      if (!option?.value) return;

      const { value, optionId } = option;

      onSelectValue(optionId, value);

      const state = {
        inputValue: "",
        selectedItem: { subText: "", value: "", optionId: "" },
      };

      // workaround to clear text box value after selecting
      downshiftHandle.setState(state);
    },
    [onSelectValue]
  );

  const cx = createClasses([
    styles.searchDropdownContainer,
    {
      [styles.disabled]: disabled,
    },
  ]);

  return (
    <div className={cx}>
      <MemoizedLineSearchDropdown
        className={styles.searchDropdown}
        onTextChange={onTextChange}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onChange={handleChange}
        id={id}
        inputProps={{ autocomplete: "off" }}
        variant="default"
        dropdownVariant="markets"
        showDropdownPlusBtn
        disabled={disabled}
        placeholder={placeholder}
        noOptionsText="No Results"
        options={suggestions}
      />
      <Plus className={styles.plusIcon} />
    </div>
  );
}

export type WatchlistCompareSearchProps = {
  className?: string;
  comparisons: WatchlistComparison[];
  onCompareSelection: (
    type: "ticker" | "watchlist",
    compareValue: string,
    compareName?: string
  ) => void;
};

export default function WatchlistCompareSearch(
  props: WatchlistCompareSearchProps
) {
  const { className, comparisons, onCompareSelection } = props;

  const [disabled, setDisabled] = useState(false);
  const [ignoredWatchlistValues, setIgnoredWatchlistValues] = useState(
    new Set<string>()
  );
  const [ignoredTickerValues, setIgnoredTickerValues] = useState(
    new Set<string>()
  );

  const { selectedPortfolio, portfolioInfos } = usePortfolioPage();

  useEffect(() => {
    // prepare a list of values, that we don't want in search results
    const ignoredWatchlistIds = new Set<string>();
    const ignoredTickers = new Set<string>();

    // add current watchlist id
    if (selectedPortfolio?.portInfo?.id) {
      ignoredWatchlistIds.add(selectedPortfolio?.portInfo.id.toString());
    }

    // add previously selected comparison values
    comparisons?.forEach((item) => {
      if (item.comparisonWatchlistId) {
        ignoredWatchlistIds.add(item.comparisonWatchlistId.toString());
      } else if (item.comparisonTicker) {
        ignoredTickers.add(item.comparisonTicker);
      }
    });

    // update state
    setIgnoredWatchlistValues(ignoredWatchlistIds);
    setIgnoredTickerValues(ignoredTickers);

    // maximum 4 comparisons are allowed
    setDisabled(
      normalizeComparison(comparisons)?.length >= MAX_COMPARISONS_ALLOWED
    );
  }, [comparisons, selectedPortfolio?.portInfo]);

  /**
   * Watchlist Search
   */
  // prepare list of user's watchlist, including current
  const userWatchlistOptions = useMemo(() => {
    return portfolioInfos.map((portInfo) => {
      return {
        value: portInfo.id.toString(),
        subText: portInfo.name,
      };
    });
  }, [portfolioInfos]);

  const fetchWatchlistSearchResults = useCallback(
    (searchString: string): Promise<DropdownOptions> => {
      if (!userWatchlistOptions?.length) {
        return Promise.resolve([]);
      }

      return Promise.resolve(
        userWatchlistOptions
          .filter((item) => {
            // ignore values that we don't want to show in results
            if (ignoredWatchlistValues.has(item.value)) {
              return false;
            }

            return (
              item.subText.toLowerCase().includes(searchString.toLowerCase()) ||
              item.value.toLowerCase().includes(searchString.toLowerCase())
            );
          })
          .map((item) => ({
            // use the watchlist name for both value and display name
            value: item.subText,
            subText: item.subText,
            // we need the watchlistId for watchlist comparisons
            optionId: item.value,
          }))
      );
    },
    [ignoredWatchlistValues, userWatchlistOptions]
  );

  /**
   * Symbol Search
   */
  const { countryCode, searchClientURL } = usePageData();
  const searchClient = useMemo(
    () =>
      new MediaServicesSearchClient({
        baseUrl: searchClientURL,
      }),
    [searchClientURL]
  );

  const fetchSymbolSearchResults = useCallback(
    async (searchQuery: string) => {
      const { results: nextSuggestions }: SearchQueryResult =
        await searchClient.lookup({
          query: searchQuery,
          includeBlocklisted: false,
          source: getSessionIdCookie(),
          size: 100,
          country: countryCode,
          types: ["Company", "Index", "Fund", "Currency"],
          boostParents: true,
        });

      if (!nextSuggestions) {
        return [];
      }

      return nextSuggestions
        .filter((item) => {
          // ignore tickers that we don't want to show in results
          return !ignoredTickerValues.has(item.resource_id);
        })
        .map(({ name, resource_id: resourceId }) => ({
          value: resourceId,
          subText: name,
          optionId: resourceId,
        }));
    },
    [searchClient, countryCode, ignoredTickerValues]
  );

  /**
   * Search Result Selection Handlers
   */
  const onAddSymbol = (optionId: string) => {
    trackClickEvent(
      "quote_add_compare",
      "add-compare-from-db",
      "click",
      "Market Data"
    );
    trackClickEvent(
      "quote_add_compared_security",
      optionId,
      "click",
      "Market Data"
    );

    // notify parent
    // e.g. {optionId: 'CAVA:US'}
    onCompareSelection("ticker", optionId);
  };

  const onAddWatchlist = (optionId: string, value: string) => {
    trackClickEvent(
      "quote_add_compare",
      "add-compare-watchlist-from-db",
      "click",
      "Market Data"
    );
    // notify parent
    // e.g. {optionId: '7920003', value: 'Global Markets'}
    onCompareSelection("watchlist", optionId, value);
  };

  return (
    <div className={createClasses([className, styles.watchlistCompareSearch])}>
      <SearchDropdown
        id="compare-symbol-search"
        disabled={disabled}
        placeholder={"Add Symbol"}
        onSelectValue={onAddSymbol}
        fetchSearchResults={fetchSymbolSearchResults}
      />

      <SearchDropdown
        id="compare-watchlist-search"
        disabled={disabled}
        placeholder={"Add Watchlist"}
        onSelectValue={onAddWatchlist}
        fetchSearchResults={fetchWatchlistSearchResults}
      />
    </div>
  );
}
